/**
 * Calendar Assistant Context
 * 
 * Provides state management and API integration for the Calendar Assistant.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { CALENDAR_SECTIONS, COMMON_CALENDAR_QUESTIONS, CALENDAR_WORKFLOWS } from './calendarKnowledge';

const generateSessionId = () => {
  return `calendar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const CalendarAssistantContext = createContext(null);

export const CalendarAssistantProvider = ({ children }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  
  // Panel state
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  
  // Session state
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  
  // Context tracking
  const [currentView, setCurrentView] = useState('Week');
  
  // Conversation state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // New messages indicator
  const [hasNewMessages, setHasNewMessages] = useState(false);
  
  // Refs
  const pendingSave = useRef(false);
  const saveTimeout = useRef(null);

  // Toggle panel
  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
    if (!isPanelOpen) {
      setHasNewMessages(false);
    }
  }, [isPanelOpen]);

  // Close panel
  const closePanel = useCallback(() => {
    if (!isPinned) {
      setIsPanelOpen(false);
    }
  }, [isPinned]);

  // Toggle pin
  const togglePin = useCallback(() => {
    setIsPinned(prev => !prev);
  }, []);

  // Unpin and close
  const unpinAndClose = useCallback(() => {
    setIsPinned(false);
    setIsPanelOpen(false);
  }, []);

  // Update current view
  const updateCurrentView = useCallback((view) => {
    setCurrentView(view);
  }, []);

  // Add message to conversation
  const addMessage = useCallback((message) => {
    setConversationHistory(prev => [...prev, {
      ...message,
      timestamp: new Date().toISOString()
    }]);
    
    // Set new messages flag if panel is closed and message is from assistant
    if (!isPanelOpen && message.role === 'assistant') {
      setHasNewMessages(true);
    }
  }, [isPanelOpen]);

  // Clear conversation
  const clearConversation = useCallback(() => {
    setConversationHistory([]);
    setSessionId(generateSessionId());
  }, []);

  // Get full context for AI
  const getFullContext = useCallback(() => {
    return {
      currentView,
      allSections: Object.keys(CALENDAR_SECTIONS).map(key => ({
        key,
        name: CALENDAR_SECTIONS[key].name,
        description: CALENDAR_SECTIONS[key].description
      })),
      commonQuestions: COMMON_CALENDAR_QUESTIONS,
      workflows: CALENDAR_WORKFLOWS,
      conversationLength: conversationHistory.length,
      sessionId
    };
  }, [currentView, conversationHistory.length, sessionId]);

  // Send message to AI
  const handleSendMessage = useCallback(async (messageText) => {
    if (!messageText?.trim() || isLoading) return;

    // Add user message
    addMessage({
      role: 'user',
      content: messageText.trim()
    });

    setIsLoading(true);

    try {
      const fullContext = getFullContext();
      
      const response = await authenticatedAxios.post('/calendar/assistant/message', {
        message: messageText.trim(),
        context: fullContext,
        conversationHistory: conversationHistory.slice(-10)
      });

      addMessage({
        role: 'assistant',
        content: response.data.message || "I'm sorry, I couldn't process that request."
      });
    } catch (error) {
      console.error('[CalendarAssistant] Error sending message:', error);
      addMessage({
        role: 'assistant',
        content: "I apologize, but I encountered an error. Please try again or refresh the page."
      });
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedAxios, conversationHistory, getFullContext, isLoading, addMessage]);

  // Save conversation (debounced)
  const saveConversation = useCallback(async () => {
    if (pendingSave.current || conversationHistory.length === 0) return;
    
    pendingSave.current = true;
    
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    
    saveTimeout.current = setTimeout(async () => {
      try {
        await authenticatedAxios.post('/calendar/assistant/conversation', {
          sessionId,
          messages: conversationHistory
        });
      } catch (error) {
        console.error('[CalendarAssistant] Error saving conversation:', error);
      } finally {
        pendingSave.current = false;
      }
    }, 2000);
  }, [authenticatedAxios, sessionId, conversationHistory]);

  const value = {
    // Panel state
    isPanelOpen,
    setIsPanelOpen,
    togglePanel,
    closePanel,
    isPinned,
    togglePin,
    unpinAndClose,
    
    // Session
    sessionId,
    
    // Context
    currentView,
    updateCurrentView,
    
    // Conversation
    conversationHistory,
    addMessage,
    clearConversation,
    isLoading,
    setIsLoading,
    
    // New messages
    hasNewMessages,
    setHasNewMessages,
    
    // Actions
    handleSendMessage,
    getFullContext,
    saveConversation
  };

  return (
    <CalendarAssistantContext.Provider value={value}>
      {children}
    </CalendarAssistantContext.Provider>
  );
};

export const useCalendarAssistant = () => {
  const context = useContext(CalendarAssistantContext);
  if (!context) {
    throw new Error('useCalendarAssistant must be used within a CalendarAssistantProvider');
  }
  return context;
};

export default CalendarAssistantContext;

